import { useCallback, useMemo } from 'react';

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { transport } from '@/shared/api';
import { ApiError } from '@/shared/api/http/client';
import { DASH } from '@/shared/api/http/map/format';
import type {
  CaseDossier,
  CaseServiceMapping,
  CatalogOfferingAdmin,
  CatalogOfferingDraft,
  CatalogOfferingPatch,
  ClarificationDraftRequest,
  CsvImportResult,
  GrantQuery,
  ListQuery,
  PageOf,
  RequesterDraft,
  RequesterStatus,
  SavedClarification,
  SignOffRecord,
  SignOffRequest,
  SiteDraft,
  SitePatch,
  TakeoverRequest,
  TenantCustomerDraft,
  TenantCustomerPatch,
} from '@/shared/api/transport';
import { countNav, type NavCounters } from '@/shared/domain/selectors';
import type { CohortMeasurements, PurgeReport } from '@/shared/domain/analytics';
import type { CaseRecord, EvidenceItem } from '@/shared/domain/case';
import type {
  BusinessRule,
  ClarificationTemplate,
  ClassificationRule,
  QuestionBank,
  Threshold,
} from '@/shared/domain/configuration';
import type { ThreadMessage } from '@/shared/domain/conversation';
import type { Customer, RequesterMaster, Site } from '@/shared/domain/customer';
import type { SessionUser } from '@/shared/domain/identity';
import type { EmailImportResult, MailRecord } from '@/shared/domain/intake';
import type { Characteristic, Offering } from '@/shared/domain/serviceCatalog';
import type { CockpitSettings } from '@/shared/domain/settings';
import type {
  AccessGrant,
  TenantChoices,
  TenantCustomer,
  TenantCustomerMasterData,
} from '@/shared/domain/tenancy';
import {
  mriFieldDraftFromAdmin,
  type MriFieldDraft,
  type MriFieldSetAdmin,
  type MriFieldSetDraft,
  type MriFieldSetKind,
  type MriFieldSetPatch,
} from '@/shared/domain/mriAdmin';
import type { AuditEvent } from '@/shared/api/transport';

export const queryKeys = {
  cases: ['cases'] as const,
  case: (id: string) => ['cases', id] as const,
  dossier: (id: string) => ['cases', id, 'dossier'] as const,
  serviceMappings: (id: string) => ['cases', id, 'service-mappings'] as const,
  openGaps: ['cases', 'open-gaps'] as const,
  threads: ['threads'] as const,
  mails: ['mails'] as const,
  session: ['session'] as const,
  cohort: (params?: { since?: string; until?: string }) => ['cohort', params ?? {}] as const,
  purgeReport: (params?: { since?: string; until?: string }) =>
    ['purge-report', params ?? {}] as const,
  offerings: ['offerings'] as const,
  catalogOffering: (id: string) => ['catalog-offering', id] as const,
  characteristics: ['characteristics'] as const,
  customers: ['customers'] as const,
  sites: ['sites'] as const,
  requesterMasters: ['requester-masters'] as const,
  evidence: ['evidence'] as const,
  classificationRules: ['classification-rules'] as const,
  businessRules: ['business-rules'] as const,
  thresholds: ['thresholds'] as const,
  templates: ['templates'] as const,
  questionBank: ['question-bank'] as const,
  settings: ['settings'] as const,
  /* Tenancy keys are deliberately not nested under any of the above.
     Everything else in this table is scoped to the chosen tenant and is thrown
     away wholesale when the operator switches; these three are not, because
     they are about the choice itself.

     Each comes in two forms, and the pairing is what makes invalidation work
     now that these reads are paged. The bare array is the root: a mutation
     invalidates it and React Query's prefix matching reaches every page and
     every search term under it. The function appends the query, so two searches
     are two cache entries rather than one that flickers between them. Writing
     only the second form would mean a mutation could invalidate the page the
     screen happened to be on and leave the others stale. */
  tenantChoices: ['tenant-choices'] as const,
  tenantChoicesPage: (query: ListQuery) => ['tenant-choices', query] as const,
  tenantCustomers: ['tenant-customers'] as const,
  tenantCustomersPage: (query: ListQuery) => ['tenant-customers', query] as const,
  tenantCustomerMasterData: ['tenant-customer-master-data'] as const,
  grants: (customerId: string) => ['grants', 'customer', customerId] as const,
  grantsPage: (customerId: string, query: GrantQuery) =>
    ['grants', 'customer', customerId, query] as const,
  /* MRI field sets: the two kinds (`full`, `termination`) read separately so a
     tab can fetch the one in view lazily, but a list call invalidates both
     because a replace supersedes one and the list reflects the new active row.
     The list key is the root a mutation invalidates; the per-kind key is the
     entry the screen reads. */
  mriFieldSets: ['mri-field-sets'] as const,
  mriFieldSet: (setKind: string) => ['mri-field-sets', setKind] as const,
  auditEvents: (caseId: string) => ['cases', caseId, 'audit-events'] as const,
};

export function useCases(): UseQueryResult<readonly CaseRecord[]> {
  return useQuery({
    queryKey: queryKeys.cases,
    queryFn: () => transport.listCases(),
  });
}

export function useCase(id: string): UseQueryResult<CaseRecord | null> {
  return useQuery({ queryKey: queryKeys.case(id), queryFn: () => transport.getCase(id) });
}

export function useMails(): UseQueryResult<readonly MailRecord[]> {
  return useQuery({ queryKey: queryKeys.mails, queryFn: () => transport.listMails() });
}

export function useUploadEmail(): UseMutationResult<EmailImportResult, Error, File> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => transport.uploadEmail(file),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.mails });
    },
  });
}

export function useReanalyseEmail(): UseMutationResult<void, Error, { caseItemId: string; requestId: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseItemId, requestId }) => transport.reanalyseEmail(caseItemId, requestId),
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.mails }),
        client.invalidateQueries({ queryKey: queryKeys.cases }),
      ]);
    },
  });
}

export function useSession(): UseQueryResult<SessionUser> {
  return useQuery({ queryKey: queryKeys.session, queryFn: () => transport.getSession() });
}

export function useCohort(
  params?: { since?: string; until?: string },
): UseQueryResult<CohortMeasurements> {
  return useQuery({
    queryKey: queryKeys.cohort(params),
    queryFn: () => transport.getCohort(params),
  });
}

export function usePurgeReport(params?: {
  since?: string;
  until?: string;
}): UseQueryResult<PurgeReport> {
  return useQuery({
    queryKey: queryKeys.purgeReport(params),
    queryFn: () => transport.getPurgeReport(params),
    placeholderData: keepPreviousData,
  });
}

export function useDossier(id: string | null): UseQueryResult<CaseDossier> {
  return useQuery({
    // The key still names the case so two open cases never share a cache entry;
    // the disabled query never runs, so the placeholder key is never fetched.
    queryKey: queryKeys.dossier(id ?? '_none'),
    queryFn: () => transport.getDossier(id as string),
    enabled: id !== null,
  });
}

export function useCaseServiceMappings(
  id: string | null,
): UseQueryResult<readonly CaseServiceMapping[]> {
  return useQuery({
    queryKey: queryKeys.serviceMappings(id ?? '_none'),
    queryFn: () => transport.listCaseServiceMappings(id as string),
    enabled: id !== null,
  });
}

export function useOpenGaps(): UseQueryResult<Readonly<Record<string, readonly string[]>>> {
  return useQuery({ queryKey: queryKeys.openGaps, queryFn: () => transport.listOpenGaps() });
}

export function useThreads(): UseQueryResult<Readonly<Record<string, readonly ThreadMessage[]>>> {
  return useQuery({ queryKey: queryKeys.threads, queryFn: () => transport.listThreads() });
}

/** What a save needs to name: the case the draft is for and the wording a person
    read. The body comes in already rendered — the cockpit sends the text that
    was on screen, not a template id the service would re-render from, because
    what was approved and what was sent must be the same string. */
export interface ClarificationDraftChange {
  readonly caseId: string;
  readonly draft: ClarificationDraftRequest;
}

export function useCreateClarification(): UseMutationResult<
  SavedClarification,
  Error,
  ClarificationDraftChange
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, draft }: ClarificationDraftChange) =>
      transport.createClarificationDraft(caseId, draft),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.threads }),
  });
}

export interface TakeOverChange {
  readonly caseId: string;
  readonly body: TakeoverRequest;
  readonly version: string;
}

export function useTakeOverCase(): UseMutationResult<CaseRecord, Error, TakeOverChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, body, version }: TakeOverChange) =>
      transport.takeOverCase(caseId, body, version),
    onSuccess: async (updated) => {
      /* Write the server's response into the per-case cache immediately so the
         page reflects 'Manually taken over · halted' on the same render as the
         modal closing — not after a background round-trip. The lists are still
         invalidated so queue views catch up too. */
      client.setQueryData<CaseRecord>(queryKeys.case(updated.id), updated);
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.case(updated.id) }),
        client.invalidateQueries({ queryKey: queryKeys.cases }),
      ]);
    },
  });
}
export interface AssignCaseChange {
  readonly caseId: string;
  readonly version: string;
  readonly owner?: string;
}

export function useAssignCase(): UseMutationResult<
  CaseRecord,
  Error,
  AssignCaseChange
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      caseId,
      version,
      owner,
    }) =>
      transport.assignCase(
        caseId,
        version,
        owner,
      ),

    onSuccess: async (updated) => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: queryKeys.case(updated.id),
        }),
        client.invalidateQueries({
          queryKey: queryKeys.cases,
        }),
      ]);
    },
  });
}


export interface SignOffChange {
  readonly caseId: string;
  readonly body: SignOffRequest;
  readonly version: string;
}

export function useSignOffCase(): UseMutationResult<SignOffRecord, Error, SignOffChange> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, body, version }: SignOffChange) =>
      transport.signOffCase(caseId, body, version),
    onSuccess: async (_result, { caseId }) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.case(caseId) }),
        client.invalidateQueries({ queryKey: queryKeys.cases }),
      ]);
    },
  });
}

/* Master data changes on a different clock from case data: hours, not seconds.
   A long stale time keeps the catalog out of the way of the queues. */
const MASTER_DATA_STALE = 30 * 60_000;

export function useOfferings(): UseQueryResult<readonly Offering[]> {
  return useQuery({
    queryKey: queryKeys.offerings,
    queryFn: () => transport.listOfferings(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useCharacteristics(): UseQueryResult<readonly Characteristic[]> {
  return useQuery({
    queryKey: queryKeys.characteristics,
    queryFn: () => transport.listCharacteristics(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useCustomers(): UseQueryResult<readonly Customer[]> {
  return useQuery({
    queryKey: queryKeys.customers,
    queryFn: () => transport.listCustomers(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useSites(): UseQueryResult<readonly Site[]> {
  return useQuery({
    queryKey: queryKeys.sites,
    queryFn: () => transport.listSites(),
    staleTime: MASTER_DATA_STALE,
  });
}

function useSiteInvalidation(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.sites }),
      client.invalidateQueries({ queryKey: queryKeys.customers }),
    ]);
  };
}

export function useCreateSite(): UseMutationResult<Site, Error, SiteDraft> {
  const invalidate = useSiteInvalidation();
  return useMutation({
    mutationFn: (draft: SiteDraft) => transport.createSite(draft),
    onSuccess: invalidate,
  });
}

export function useCreateSites(): UseMutationResult<
  readonly Site[],
  Error,
  readonly SiteDraft[]
> {
  const invalidate = useSiteInvalidation();
  return useMutation({
    mutationFn: (drafts: readonly SiteDraft[]) => transport.createSites(drafts),
    onSuccess: invalidate,
  });
}

export interface SiteChange {
  readonly id: string;
  readonly patch: SitePatch;
  readonly version: number;
}

export function useUpdateSite(): UseMutationResult<Site, Error, SiteChange> {
  const invalidate = useSiteInvalidation();
  return useMutation({
    mutationFn: ({ id, patch, version }: SiteChange) => transport.updateSite(id, patch, version),
    onSuccess: invalidate,
  });
}

/** Customer id → the name the customer is known by, for the cells that hold a
    tenant reference.

    The id is a join key and never a label. It is a uuid: putting it in a cell
    tells the reader nothing they can act on, and costs them the one string they
    could have recognised. The fallback is a dash rather than the id, including
    while the customer list is still arriving — an unresolvable tenant is a
    finding, and a uuid is not a better account of it than saying so.

    Returned as a stable callback so a column definition can be memoised on it. */
export function useTenantNames(): (id: string | null) => string {
  const customers = useCustomers();

  const names = useMemo(
    () => new Map((customers.data ?? []).map((customer) => [customer.id, customer.name])),
    [customers.data],
  );

  return useCallback((id: string | null) => (id === null ? DASH : (names.get(id) ?? DASH)), [names]);
}

export function useRequesterMasters(): UseQueryResult<readonly RequesterMaster[]> {
  return useQuery({
    queryKey: queryKeys.requesterMasters,
    queryFn: () => transport.listRequesterMasters(),
    staleTime: MASTER_DATA_STALE,
  });
}
/* The register is the one master-data set the cockpit writes to, so it is the
   one place a mutation exists. Both writes invalidate the same two keys: the
   register itself, and the customer list — whose "Requester IDs" column is a
   count of the active rows in it, and would otherwise sit half an hour behind
   the change the operator just watched happen. */

function useRequesterInvalidation(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.requesterMasters }),
      client.invalidateQueries({ queryKey: queryKeys.customers }),
    ]);
  };
}

export function useCreateRequesterMaster(): UseMutationResult<
  RequesterMaster,
  Error,
  RequesterDraft
> {
  const invalidate = useRequesterInvalidation();
  return useMutation({
    mutationFn: (draft: RequesterDraft) => transport.createRequesterMaster(draft),
    onSuccess: invalidate,
  });
}

/** What a status change needs to name: which requester, which state, and which
    version it believed it was changing. The version is not looked up here — a
    hook that fetched a fresh one would be sending "whatever it is now", which is
    exactly the blind overwrite the precondition refuses. */
export interface RequesterStatusChange {
  readonly id: string;
  readonly status: RequesterStatus;
  readonly version: number;
}

export function useSetRequesterMasterStatus(): UseMutationResult<
  RequesterMaster,
  Error,
  RequesterStatusChange
> {
  const invalidate = useRequesterInvalidation();
  return useMutation({
    mutationFn: ({ id, status, version }: RequesterStatusChange) =>
      transport.setRequesterMasterStatus(id, status, version),
    onSuccess: invalidate,
  });
}

export function useEvidence(): UseQueryResult<Readonly<Record<string, EvidenceItem>>> {
  return useQuery({
    queryKey: queryKeys.evidence,
    queryFn: () => transport.getEvidence(),
    staleTime: MASTER_DATA_STALE,
  });
}

/* Administered configuration moves slower still — a rule set is versioned and
   published, not edited under an operator. It shares the master-data clock. */

export function useClassificationRules(): UseQueryResult<readonly ClassificationRule[]> {
  return useQuery({
    queryKey: queryKeys.classificationRules,
    queryFn: () => transport.listClassificationRules(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useBusinessRules(): UseQueryResult<readonly BusinessRule[]> {
  return useQuery({
    queryKey: queryKeys.businessRules,
    queryFn: () => transport.listBusinessRules(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useThresholds(): UseQueryResult<readonly Threshold[]> {
  return useQuery({
    queryKey: queryKeys.thresholds,
    queryFn: () => transport.listThresholds(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useTemplates(): UseQueryResult<readonly ClarificationTemplate[]> {
  return useQuery({
    queryKey: queryKeys.templates,
    queryFn: () => transport.listTemplates(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useQuestionBank(): UseQueryResult<QuestionBank> {
  return useQuery({
    queryKey: queryKeys.questionBank,
    queryFn: () => transport.getQuestionBank(),
    staleTime: MASTER_DATA_STALE,
  });
}

export function useSettings(): UseQueryResult<CockpitSettings> {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => transport.getSettings(),
    staleTime: MASTER_DATA_STALE,
  });
}

/** Sidebar badges. Null until all three inputs have arrived, so a badge never
    shows a count derived from a half-loaded dataset. */
export function useNavCounters(): NavCounters | null {
  const cases = useCases();
  const mails = useMails();
  const session = useSession();

  if (!cases.data || !mails.data || !session.data) return null;
  return countNav(cases.data, mails.data, session.data.name);
}

/* ---------- tenancy ---------- */

/* No `staleTime` on any of these, which is the opposite of the master-data
   choice above and deliberate.
 *
 * Master data is read constantly and changes hourly, so half an hour of
 * staleness buys a great deal and costs nothing anybody notices. These three are
 * read on two screens, and both of those screens are where somebody has gone
 * *because* something changed: an administrator who has just granted access and
 * an operator who has just been told they now have a second tenant. Serving
 * either of them a cached answer is serving them the exact answer they came to
 * stop seeing.
 */

/* All three take a query and key on it, and all three keep the previous page on
   screen while the next one loads.
 *
 * `placeholderData: keepPreviousData` is what stops a table blanking between
 * pages. Without it every keystroke in a search box unmounts the rows, draws the
 * empty state, and draws them again — which reads as the data being lost rather
 * than as it being narrowed. The rows shown during that moment are the previous
 * query's, and `isPlaceholderData` is how a screen can say so.
 */

/** The tenants this operator may choose between. Answers before a tenant has
    been chosen, which is what makes the picker reachable at all. */
export function useTenantChoices(query: ListQuery = {}): UseQueryResult<TenantChoices> {
  return useQuery({
    queryKey: queryKeys.tenantChoicesPage(query),
    queryFn: () => transport.listTenantChoices(query),
    placeholderData: keepPreviousData,
  });
}

/** Every tenant in the deployment. Administrators only — the API refuses
    anybody else, so the screen this feeds is gated on `tenant:administer`
    rather than left to fail. */
export function useTenantCustomers(
  query: ListQuery = {},
  enabled = true,
): UseQueryResult<PageOf<TenantCustomer>> {
  return useQuery({
    queryKey: queryKeys.tenantCustomersPage(query),
    queryFn: () => transport.listTenantCustomers(query),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useTenantCustomerMasterData(
  enabled = true,
): UseQueryResult<TenantCustomerMasterData> {
  return useQuery({
    queryKey: queryKeys.tenantCustomerMasterData,
    queryFn: () => transport.getTenantCustomerMasterData(),
    enabled,
    staleTime: MASTER_DATA_STALE,
  });
}

/** Who may reach one tenant. `null` while no tenant is selected on the screen,
    which is the state the access page opens in. */
export function useGrants(
  customerId: string | null,
  query: GrantQuery = {},
): UseQueryResult<PageOf<AccessGrant>> {
  return useQuery({
    queryKey: queryKeys.grantsPage(customerId ?? '_none', query),
    queryFn: () => transport.listGrantsForCustomer(customerId as string, query),
    enabled: customerId !== null,
    placeholderData: keepPreviousData,
  });
}

/* Both grant writes invalidate the same three keys, for three different
   reasons: the grant list is what changed, the tenant list carries a count of
   live grants per tenant, and the operator's own choices may now include a
   tenant they could not reach a moment ago — which is only visibly true if the
   picker is re-read. */
function useGrantInvalidation(customerId: string): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.grants(customerId) }),
      client.invalidateQueries({ queryKey: queryKeys.tenantCustomers }),
      client.invalidateQueries({ queryKey: queryKeys.tenantChoices }),
    ]);
  };
}

function useTenantInvalidation(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.tenantCustomers }),
      client.invalidateQueries({ queryKey: queryKeys.tenantChoices }),
    ]);
  };
}

export function useCreateTenantCustomer(): UseMutationResult<
  TenantCustomer,
  Error,
  TenantCustomerDraft
> {
  const invalidate = useTenantInvalidation();
  return useMutation({
    mutationFn: (draft: TenantCustomerDraft) => transport.createTenantCustomer(draft),
    onSuccess: invalidate,
  });
}

export function useCreateTenantCustomers(): UseMutationResult<
  readonly TenantCustomer[],
  Error,
  readonly TenantCustomerDraft[]
> {
  const invalidate = useTenantInvalidation();
  return useMutation({
    mutationFn: (drafts) => transport.createTenantCustomers(drafts),
    onSuccess: invalidate,
  });
}

function useCatalogInvalidation(id?: string): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.offerings }),
      client.invalidateQueries({ queryKey: queryKeys.characteristics }),
      ...(id
        ? [client.invalidateQueries({ queryKey: queryKeys.catalogOffering(id) })]
        : []),
    ]);
  };
}

export function useCatalogOffering(
  id: string | null,
  enabled = true,
): UseQueryResult<CatalogOfferingAdmin> {
  return useQuery({
    queryKey: queryKeys.catalogOffering(id ?? '_none'),
    queryFn: () => transport.getCatalogOffering(id as string),
    enabled: enabled && id !== null,
  });
}

export function useCreateCatalogOffering(): UseMutationResult<
  CatalogOfferingAdmin,
  Error,
  CatalogOfferingDraft
> {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (draft) => transport.createCatalogOffering(draft),
    onSuccess: invalidate,
  });
}

export function useCreateCatalogOfferings(): UseMutationResult<
  readonly CatalogOfferingAdmin[],
  Error,
  readonly CatalogOfferingDraft[]
> {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (drafts) => transport.createCatalogOfferings(drafts),
    onSuccess: invalidate,
  });
}

/** Import service offerings from a CSV file. On success, invalidates the catalog
    lists so newly imported rows appear immediately. Partial success is normal:
    rows in `result.errors` did not import and stay visible to the caller. */
export function useImportCatalogCsv(): UseMutationResult<CsvImportResult, Error, File> {
  const invalidate = useCatalogInvalidation();
  return useMutation({
    mutationFn: (file: File) => transport.importCatalogCsv(file),
    onSuccess: invalidate,
  });
}

export interface CatalogOfferingChange {
  readonly id: string;
  readonly patch: CatalogOfferingPatch;
  readonly version: number;
}

export function useUpdateCatalogOffering(
  id?: string,
): UseMutationResult<CatalogOfferingAdmin, Error, CatalogOfferingChange> {
  const invalidate = useCatalogInvalidation(id);
  return useMutation({
    mutationFn: (change) =>
      transport.updateCatalogOffering(change.id, change.patch, change.version),
    onSuccess: invalidate,
  });
}

/** What a tenant edit names: which tenant, what to change, and the version it
    believed it was changing. Same shape and same reasoning as
    `RequesterStatusChange` — the version comes from the row on screen, never
    from a fresh read. */
export interface TenantCustomerChange {
  readonly id: string;
  readonly patch: TenantCustomerPatch;
  readonly version: number;
}

export function useUpdateTenantCustomer(): UseMutationResult<
  TenantCustomer,
  Error,
  TenantCustomerChange
> {
  const invalidate = useTenantInvalidation();
  return useMutation({
    mutationFn: ({ id, patch, version }: TenantCustomerChange) =>
      transport.updateTenantCustomer(id, patch, version),
    onSuccess: invalidate,
  });
}

export function useGrantAccess(customerId: string): UseMutationResult<AccessGrant, Error, string> {
  const invalidate = useGrantInvalidation(customerId);
  return useMutation({
    mutationFn: (subject: string) => transport.grantAccess(subject, customerId),
    onSuccess: invalidate,
  });
}

export function useWithdrawAccess(
  customerId: string,
): UseMutationResult<AccessGrant, Error, string> {
  const invalidate = useGrantInvalidation(customerId);
  return useMutation({
    mutationFn: (grantId: string) => transport.withdrawAccess(grantId),
    onSuccess: invalidate,
  });
}

/* ---------- MRI field sets ---------- */

/** Invalidate both the list and the per-kind entries. A replace supersedes
    one kind's active row, but the list endpoint returns both kinds and any
    tab the operator is not on still holds the old row until this fires. */
function useMriFieldSetInvalidation() {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: queryKeys.mriFieldSets });
  };
}

export function useMriFieldSets(): UseQueryResult<readonly MriFieldSetAdmin[]> {
  return useQuery({
    queryKey: queryKeys.mriFieldSets,
    queryFn: () => transport.listMriFieldSets(),
    staleTime: 0,
  });
}

export function useMriFieldSet(
  setKind: MriFieldSetKind,
): UseQueryResult<MriFieldSetAdmin> {
  return useQuery({
    queryKey: queryKeys.mriFieldSet(setKind),
    queryFn: () => transport.getMriFieldSet(setKind),
    staleTime: 0,
  });
}

/** What a set replace names: the kind, the full draft, and the version the
    caller read. The service supersedes the previous active row atomically and
  refuses a stale replace with a 409. */
export interface MriFieldSetReplace {
  readonly setKind: MriFieldSetKind;
  readonly draft: MriFieldSetDraft;
  readonly version: number;
}

export function usePutMriFieldSet(): UseMutationResult<
  MriFieldSetAdmin,
  Error,
  MriFieldSetReplace
> {
  const invalidate = useMriFieldSetInvalidation();
  return useMutation({
    mutationFn: (change) => transport.putMriFieldSet(change.setKind, change.draft, change.version),
    onSettled: invalidate,
  });
}

export interface MriFieldRequirednessChange {
  readonly setKind: MriFieldSetKind;
  readonly fieldName: string;
  readonly required: boolean;
}

export function useSetMriFieldRequiredness(): UseMutationResult<
  MriFieldSetAdmin,
  Error,
  MriFieldRequirednessChange
> {
  const invalidate = useMriFieldSetInvalidation();
  return useMutation({
    mutationFn: async (change) => {
      const current = await transport.getMriFieldSet(change.setKind);
      const field = current.fields.find((candidate) => candidate.name === change.fieldName);
      if (!field) throw new Error('The MRI field no longer exists. Refresh and try again.');
      const minCardinality = change.required ? 1 : 0;
      if ((field.minCardinality >= 1) === change.required) return current;
      return transport.putMriFieldSet(
        change.setKind,
        {
          setKind: current.setKind,
          name: current.name,
          fields: current.fields.map((candidate) => ({
            ...mriFieldDraftFromAdmin(candidate),
            minCardinality:
              candidate.name === change.fieldName ? minCardinality : candidate.minCardinality,
          })),
        },
        current.versionNo,
      );
    },
    retry: (failureCount, error) =>
      failureCount === 0 && error instanceof ApiError && error.isConflict,
    retryDelay: 0,
    onSettled: invalidate,
  });
}

/** What a set rename names: the kind, the patch, and the version the caller
    read. Field-level edits use the field sub-resource hooks below. */
export interface MriFieldSetRename {
  readonly setKind: MriFieldSetKind;
  readonly patch: MriFieldSetPatch;
  readonly version: number;
}

export function usePatchMriFieldSet(): UseMutationResult<
  MriFieldSetAdmin,
  Error,
  MriFieldSetRename
> {
  const invalidate = useMriFieldSetInvalidation();
  return useMutation({
    mutationFn: (change) =>
      transport.patchMriFieldSet(change.setKind, change.patch, change.version),
    onSuccess: invalidate,
  });
}

export function useAddMriField(
  setKind: MriFieldSetKind,
): UseMutationResult<MriFieldSetAdmin, Error, MriFieldDraft> {
  const invalidate = useMriFieldSetInvalidation();
  return useMutation({
    mutationFn: (field) => transport.addMriField(setKind, field),
    onSuccess: invalidate,
  });
}

export function useRemoveMriField(
  setKind: MriFieldSetKind,
): UseMutationResult<void, Error, string> {
  const invalidate = useMriFieldSetInvalidation();
  return useMutation({
    mutationFn: (fieldId) => transport.removeMriField(setKind, fieldId),
    onSuccess: invalidate,
  });
}

/* ---------- audit events ---------- */

/** All audit events for one case, keyed by the internal case uuid.
 *
 * The case workspace already has the internal id via the dossier; the tab
 * passes it down so this hook never has to resolve a reference. `null` while
 * the dossier is still loading. */
export function useAuditEvents(caseId: string | null): UseQueryResult<readonly AuditEvent[]> {
  return useQuery({
    queryKey: queryKeys.auditEvents(caseId ?? '_none'),
    queryFn: () => transport.listAuditEvents(caseId as string),
    enabled: caseId !== null,
  });
}
